import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGetQueryParams } from '@/hooks/queryParams/useGetQueryParams';
import { HistoryMethod, ModifyQueryParamsAction, useModifyQueryParams } from '@/hooks/queryParams/useModifyQueryParams';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    fetchCatalogsNearMeByBounds,
    fetchCatalogsNearMeByCityAndState,
} from '@/redux/modules/auctionsNearMe/auctionsNearMe.actions';
import {
    getCatalogsNearMeSelector,
    getLoadingSelector,
    getMapPointsSelector,
    getPaginationInfoSelector,
    getTotalUniqueHousesSelector,
} from '@/redux/modules/auctionsNearMe/auctionsNearMe.selectors';
import {
    CatalogNearMe,
    type CatalogsNearMeBoundingBoxRequest,
    type CatalogsNearMeCityStateRequest,
} from '@/redux/modules/auctionsNearMe/catalogsNearMe.types';
import { getLocale } from '@/redux/modules/intl';
import { getPageFromQueryParams } from '@/utils/queryParams';

import { MapBounds } from '../components/MapView/components/GoogleMap/AuctionsNearMeGoogleMap';
import {
    defaultAuctionsNearMeUkBounds,
    defaultAuctionsNearMeUsaBounds,
    defaultPageNumber,
} from '../constants/AuctionsNearMeConstants';
import { createBoundingBoxString } from '../helpers/createBoundingBoxString';
import { getStateCodeFromName } from '../helpers/geoAreaToUrlParams';
import { getPageSizeFromQueryParams } from '../helpers/getPageSizeFromQueryParams';
import { isPlausibleLocation } from '../helpers/isPlausibleLocation';
import { AuctionsNearMeQueryParams } from '../types/AuctionsNearMe.types';
import { useGeoAreaFromUrl } from './useGeoAreaFromUrl';
import { useGeocodeLocation } from './useGeocodeLocation';

/**
 * Where the next results fetch should look. A location URL with no `geoArea` record has no bounds
 * to search — only the backend can resolve it — so the fetch has to be able to express both.
 */
type AuctionsNearMeFetchTarget = { bounds: MapBounds } | { city?: string; state: string };

export const useAuctionsNearMeData = () => {
    const dispatch = useAppDispatch();
    const getQueryParams = useGetQueryParams<AuctionsNearMeQueryParams>();
    const queryParams = getQueryParams();
    const modifyQueryParams = useModifyQueryParams();

    // Extracted hooks
    const { geoArea, hasNewUrlParams, hasOldUrlParams, routeParams } = useGeoAreaFromUrl();

    // Redux selectors
    const catalogsNearMe = useAppSelector(getCatalogsNearMeSelector);
    const locale = useAppSelector(getLocale);
    const loading = useAppSelector(getLoadingSelector);
    const mapPoints = useAppSelector(getMapPointsSelector);
    const { totalRecords } = useAppSelector(getPaginationInfoSelector);
    const totalUniqueHouses = useAppSelector(getTotalUniqueHousesSelector);

    // Local state
    const [googleMapsReady, setGoogleMapsReady] = useState(false);
    const [customSearchBounds, setCustomSearchBounds] = useState<MapBounds | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [geocoderFailed, setGeocoderFailed] = useState(false);

    // Filter state (sent to backend)
    const [topRatedFilter, setTopRatedFilter] = useState(false);
    const [localPickupFilter, setLocalPickupFilter] = useState(false);
    const [keywords, setKeywords] = useState<string[]>([]);

    // Pagination state — initialize from the URL's ?page= so SSR and the client agree on which
    // page's data was fetched. The sync effect below keeps it in step with the URL afterward
    // (browser back/forward, a bookmarked link); Pagination's own query-string push
    // (shouldModifyQueryParams in AuctionsNearMeCardsViewNew) is what changes ?page= on a click.
    const urlPageNumber = getPageFromQueryParams(queryParams);
    const urlPageSize = getPageSizeFromQueryParams(queryParams);
    const [pageNumber, setPageNumber] = useState(urlPageNumber);
    const [pageSize, setPageSize] = useState(urlPageSize);

    // resetPageNumber reads these via refs, not as useCallback deps — both urlPageNumber and
    // modifyQueryParams (useModifyQueryParams recomputes its returned function on every
    // location.search change) change identity on every page navigation. Depending on them
    // directly made resetPageNumber itself change identity on every click, which cascaded into
    // handleKeywordsChange and retriggered AuctionsNearMeFilterBar's
    // `useEffect(() => onKeywordsChange(keywords), [keywords, onKeywordsChange])` on every
    // pagination click — reporting keywords=[] again and immediately reverting the page change
    // the user just made.
    const urlPageNumberRef = useRef(urlPageNumber);
    urlPageNumberRef.current = urlPageNumber;
    const modifyQueryParamsRef = useRef(modifyQueryParams);
    modifyQueryParamsRef.current = modifyQueryParams;

    // A filter/bounds/search change resets to page 1 — clear a lingering ?page= from the URL in
    // the same step, or the address bar (and canonical) would keep pointing at the old page while
    // page 1 of the new results renders. No-op when the URL has no page to clear.
    const resetPageNumber = useCallback(() => {
        setPageNumber(defaultPageNumber);
        if (urlPageNumberRef.current !== defaultPageNumber) {
            // REPLACE, not the default push — this is a side effect of the filter/bounds/search
            // change, not a navigation of its own. A push here would make Back land back on the
            // stale ?page=N with the filter still applied, instead of undoing the filter toggle.
            modifyQueryParamsRef.current({
                action: ModifyQueryParamsAction.REMOVE,
                method: HistoryMethod.REPLACE,
                params: 'page',
            });
        }
    }, []);

    // Refs — use refs for filter/pagination values so fetchCatalogs identity stays stable
    const lastFetchParamsRef = useRef<string | null>(null);
    const currentActiveBoundsRef = useRef<MapBounds | null>(null);
    const hasUserInteractedWithMapRef = useRef(false);
    const filtersRef = useRef({ keywords, localPickupFilter, pageNumber, pageSize, topRatedFilter });
    filtersRef.current = { keywords, localPickupFilter, pageNumber, pageSize, topRatedFilter };

    // Derived values
    const catalogIds = useMemo(() => catalogsNearMe.map((catalog: CatalogNearMe) => catalog.id), [catalogsNearMe]);
    const hasFilteredResults = catalogsNearMe.length > 0;

    const defaultBounds = useMemo(
        () => (locale === 'en-gb' ? defaultAuctionsNearMeUkBounds : defaultAuctionsNearMeUsaBounds),
        [locale]
    );

    // Stable fetch function — reads filter/pagination from refs
    const fetchCatalogs = useCallback(
        (target: AuctionsNearMeFetchTarget) => {
            const {
                keywords: kw,
                localPickupFilter: lp,
                pageNumber: pn,
                pageSize: ps,
                topRatedFilter: tr,
            } = filtersRef.current;
            const filters = {
                ...(lp && { hasFreeLocalPickup: true }),
                ...(tr && { isTopRated: true }),
                ...(kw.length > 0 && { searchTerm: kw.join(',') }),
            };
            const request: CatalogsNearMeBoundingBoxRequest | CatalogsNearMeCityStateRequest =
                'bounds' in target
                    ? { insideBoundingBox: createBoundingBoxString(target.bounds), page: pn, pageSize: ps, ...filters }
                    : { city: target.city, page: pn, pageSize: ps, state: target.state, ...filters };
            // Keying off the request itself, rather than a parallel copy of its fields, keeps the
            // dedupe from going stale when a new parameter is added to one and not the other.
            const fetchKey = JSON.stringify(request);

            if (lastFetchParamsRef.current === fetchKey) {
                return;
            }

            lastFetchParamsRef.current = fetchKey;

            dispatch(
                'insideBoundingBox' in request
                    ? fetchCatalogsNearMeByBounds(request)
                    : fetchCatalogsNearMeByCityAndState(request)
            );
        },
        [dispatch]
    );

    // The location this URL stands for, when the backend is the only thing that can resolve it.
    // Only 24 US states and 29 cities have a geoArea record, so most location pages have no bounds
    // of their own — and falling back to the country-wide default replaced the location's results
    // with a nationwide list on the first filter or page change (SEAR-1879).
    const urlLocation = useMemo((): { city?: string; state: string } | null => {
        // The ?search= flow geocodes its own bounds, and legacy /{geoAreaId}/{geoAreaName}/ URLs
        // carry an id rather than a place name — both resolve without the city/state endpoint.
        if (queryParams.search || hasOldUrlParams) {
            return null;
        }

        const city = 'city' in routeParams ? routeParams.city : undefined;
        const country = 'country' in routeParams ? routeParams.country : undefined;
        const state = 'state' in routeParams ? routeParams.state : undefined;

        if (city && state) {
            return isPlausibleLocation(city, state) ? { city, state: getStateCodeFromName(state) } : null;
        }

        // `:state` and `:country` are same-rank routes matched in declaration order, so a single
        // segment lands in `state` today — read both so a route reorder cannot silently break this.
        const stateCode = getStateCodeFromName(state || country || '');

        return stateCode ? { state: stateCode } : null;
    }, [routeParams, queryParams.search, hasOldUrlParams]);

    // Get the target the next fetch should search
    const getActiveFetchTarget = useCallback((): AuctionsNearMeFetchTarget => {
        if (geoArea?.northEastLatitude && geoArea?.southWestLatitude) {
            return {
                bounds: {
                    northEast: { lat: geoArea.northEastLatitude, lng: geoArea.northEastLongitude },
                    southWest: { lat: geoArea.southWestLatitude, lng: geoArea.southWestLongitude },
                },
            };
        }
        if (currentActiveBoundsRef.current) {
            return { bounds: currentActiveBoundsRef.current };
        }
        if (customSearchBounds) {
            return { bounds: customSearchBounds };
        }
        if (urlLocation) {
            return urlLocation;
        }
        return { bounds: defaultBounds };
    }, [customSearchBounds, geoArea, defaultBounds, urlLocation]);

    // Determine location to geocode. Only the ?search= custom flow needs this: the data loader
    // (auctionsNearMePageData.ts) already resolves every /:state/:city/ and /:state/ URL precisely
    // via the backend (fetchCatalogsNearMeByCityAndState), so geocoding a canonical URL would just
    // redundantly re-fetch and overwrite that SSR result with a bounds-based approximation.
    const locationToGeocode = useMemo(() => {
        if (queryParams.search && !customSearchBounds) {
            return queryParams.search;
        }

        return null;
    }, [queryParams.search, customSearchBounds]);

    // Geocode location
    const {
        bounds: geocodedBounds,
        failed: geocodingFailed,
        isGeocoding,
    } = useGeocodeLocation({
        googleMapsReady,
        location: locationToGeocode,
    });

    // Sync geocoding failed state from hook
    useEffect(() => {
        if (geocodingFailed) {
            setGeocoderFailed(true);
        }
    }, [geocodingFailed]);

    // Sync geocoded bounds and search term when geocoding completes
    useEffect(() => {
        if (!geocodedBounds || !locationToGeocode) {
            return;
        }

        // Only set searchTerm for ?search= query param flows
        if (queryParams.search) {
            setSearchTerm(locationToGeocode);
        }
        setCustomSearchBounds(geocodedBounds);
        currentActiveBoundsRef.current = geocodedBounds;
        fetchCatalogs({ bounds: geocodedBounds });
    }, [geocodedBounds, locationToGeocode, queryParams.search, fetchCatalogs]);

    // Sync geocoder failed state and search term
    useEffect(() => {
        if (geocoderFailed && locationToGeocode && queryParams.search) {
            setSearchTerm(locationToGeocode);
        }
    }, [geocoderFailed, locationToGeocode, queryParams.search]);

    // Calculate map bounds
    const mapBounds = useMemo(() => {
        if (geoArea?.northEastLatitude && geoArea?.southWestLatitude) {
            return {
                northEast: { lat: geoArea.northEastLatitude, lng: geoArea.northEastLongitude },
                southWest: { lat: geoArea.southWestLatitude, lng: geoArea.southWestLongitude },
            };
        }

        if (customSearchBounds) {
            return customSearchBounds;
        }

        return defaultBounds;
    }, [customSearchBounds, geoArea, defaultBounds]);

    // Handlers
    const handleSearchResults = useCallback(
        ({ northEast, southWest }: MapBounds) => {
            const bounds = { northEast, southWest };
            currentActiveBoundsRef.current = bounds;
            setCustomSearchBounds(bounds);
            hasUserInteractedWithMapRef.current = true;
            resetPageNumber();
            // fetchCatalogs will be called by the filter/pagination effect
        },
        [resetPageNumber]
    );

    const handleBoundsChange = useCallback(
        ({ northEast, southWest }: MapBounds) => {
            // Skip if we're waiting for geocoding to complete
            if (locationToGeocode && !customSearchBounds) {
                return;
            }

            // Skip programmatic bounds changes (initial fitBounds settling)
            if (!hasUserInteractedWithMapRef.current) {
                return;
            }

            const bounds = { northEast, southWest };
            currentActiveBoundsRef.current = bounds;
            // Update ref eagerly so fetchCatalogs reads the reset page, not a stale value
            filtersRef.current = { ...filtersRef.current, pageNumber: defaultPageNumber };
            resetPageNumber();
            fetchCatalogs({ bounds });
        },
        [fetchCatalogs, locationToGeocode, customSearchBounds, resetPageNumber]
    );

    const handleUserMapInteraction = useCallback(() => {
        hasUserInteractedWithMapRef.current = true;
    }, []);

    const handleGoogleMapsReady = useCallback(() => {
        setGoogleMapsReady(true);
    }, []);

    // Filter handlers — re-fetch from backend, reset to page 1
    const toggleTopRatedFilter = useCallback(() => {
        setTopRatedFilter((prev) => !prev);
        resetPageNumber();
    }, [resetPageNumber]);

    const toggleLocalPickupFilter = useCallback(() => {
        setLocalPickupFilter((prev) => !prev);
        resetPageNumber();
    }, [resetPageNumber]);

    const handleKeywordsChange = useCallback(
        (newKeywords: string[]) => {
            setKeywords(newKeywords);
            resetPageNumber();
        },
        [resetPageNumber]
    );

    // Pagination handlers
    const handlePageChange = useCallback((page: number) => {
        setPageNumber(page);
        window.scrollTo(0, 0);
    }, []);

    // Deliberately not resetPageNumber: Pagination already writes `page: 1` alongside the new
    // pageSize. Removing `page` here as well would build its own query string from the same
    // pre-navigation snapshot and land last, dropping the pageSize the user just picked.
    const handlePageSizeChange = useCallback((newPageSize: number) => {
        setPageSize(newPageSize);
        setPageNumber(defaultPageNumber);
    }, []);

    // Build a stable key from route params to detect navigation between different URLs
    const routeKey = useMemo(() => {
        if ('geoAreaId' in routeParams && routeParams.geoAreaId) {
            return `old:${routeParams.geoAreaId}`;
        }
        if ('city' in routeParams || 'state' in routeParams || 'country' in routeParams) {
            const city = 'city' in routeParams ? routeParams.city : '';
            const state = 'state' in routeParams ? routeParams.state : '';
            const country = 'country' in routeParams ? routeParams.country : '';
            return `new:${city}:${state}:${country}`;
        }
        return '';
    }, [routeParams]);

    // Seeded with the mount-time routeKey so the reset below fires only on a genuine change.
    const previousRouteKeyRef = useRef(routeKey);

    // Skip all fetch effects on initial mount — the data loader already fetched.
    // The effect that sets this to true is declared AFTER the effects that check it,
    // so they all see `false` on the initial render's effect pass.
    const hasMountedRef = useRef(false);

    // Effect: Keep pageNumber in sync with the URL — covers browser back/forward and a
    // bookmarked/pasted ?page=N link, neither of which calls handlePageChange directly.
    useEffect(() => {
        setPageNumber(urlPageNumber);
    }, [urlPageNumber]);

    // Same for ?pageSize= — the loader no longer re-runs to correct it on a query-only navigation.
    useEffect(() => {
        setPageSize(urlPageSize);
    }, [urlPageSize]);

    // Effect: Clear custom search bounds when navigating to a predefined geo area.
    // MUST stay declared above the re-fetch effect below. A location change updates
    // getActiveFetchTarget's identity during render, so the re-fetch effect fires in this same
    // commit — React runs effects in declaration order, and if it ran first it would read the
    // previous location's filters off filtersRef and send an extra, wrongly-filtered request
    // for the new location before this reset corrected it on the following pass.
    useEffect(() => {
        if (!routeKey) {
            return;
        }

        setCustomSearchBounds(null);
        setSearchTerm('');
        currentActiveBoundsRef.current = null;
        lastFetchParamsRef.current = null;
        hasUserInteractedWithMapRef.current = false;

        // Only on a real move between two location URLs, never on mount. The filters are pure
        // React state that never reaches the URL, so without this they follow the user to the
        // next location — and because the loader re-runs on a pathname change, its unfiltered
        // fetch would then disagree with this hook's filtered one for the same place.
        // pageNumber/pageSize are deliberately not reset here: they are URL-driven, and the
        // ?page=/?pageSize= sync effects above already track them. Resetting them on mount is
        // what would break a directly-opened paginated URL (SEAR-1845).
        if (previousRouteKeyRef.current === routeKey) {
            return;
        }
        previousRouteKeyRef.current = routeKey;

        setTopRatedFilter(false);
        setLocalPickupFilter(false);
        setKeywords([]);
        // Update the ref eagerly so a fetch dispatched in this same pass reads the cleared
        // filters rather than the previous location's.
        filtersRef.current = { ...filtersRef.current, keywords: [], localPickupFilter: false, topRatedFilter: false };
    }, [routeKey]);

    // Re-fetch when filters or pagination change
    useEffect(() => {
        if (!hasMountedRef.current) {
            return;
        }
        fetchCatalogs(getActiveFetchTarget());
    }, [topRatedFilter, localPickupFilter, keywords, pageNumber, pageSize, getActiveFetchTarget, fetchCatalogs]);

    // Effect: Reset to defaults when navigating to base page
    useEffect(() => {
        if (hasNewUrlParams || hasOldUrlParams || queryParams.search || geoArea) {
            return;
        }

        if (customSearchBounds) {
            setCustomSearchBounds(null);
            setSearchTerm('');
            currentActiveBoundsRef.current = null;
            lastFetchParamsRef.current = null;
            hasUserInteractedWithMapRef.current = false;
            fetchCatalogs({ bounds: defaultBounds });
        }
    }, [
        hasNewUrlParams,
        hasOldUrlParams,
        queryParams.search,
        geoArea,
        customSearchBounds,
        defaultBounds,
        fetchCatalogs,
    ]);

    // Effect: Fetch catalogs for predefined geoArea (from RelatedGeoAreas links)
    useEffect(() => {
        if (!hasMountedRef.current) {
            return;
        }

        if (!geoArea || customSearchBounds || queryParams.search) {
            return;
        }

        if (!geoArea.northEastLatitude || !geoArea.southWestLatitude) {
            return;
        }

        const bounds: MapBounds = {
            northEast: { lat: geoArea.northEastLatitude, lng: geoArea.northEastLongitude },
            southWest: { lat: geoArea.southWestLatitude, lng: geoArea.southWestLongitude },
        };

        fetchCatalogs({ bounds });
    }, [geoArea, customSearchBounds, queryParams.search, fetchCatalogs]);

    // Mark as mounted AFTER all mount-skipping effects so they see false on the first pass
    useEffect(() => {
        hasMountedRef.current = true;
    }, []);

    return {
        catalogIds,
        catalogsNearMe,
        geoArea,
        geocoderFailed,
        handleBoundsChange,
        handleGoogleMapsReady,
        handleKeywordsChange,
        handlePageChange,
        handlePageSizeChange,
        handleSearchResults,
        handleUserMapInteraction,
        hasFilteredResults,
        isGeocoding,
        loading,
        mapBounds,
        mapPoints,
        pageNumber,
        pageSize,
        searchTerm,
        setGeocoderFailed,
        setSearchTerm,
        toggleLocalPickupFilter,
        toggleTopRatedFilter,
        totalRecords,
        totalUniqueHouses,
    };
};
